import React, { Fragment, PureComponent } from "react";
import { BarChart, Bar, Tooltip, ResponsiveContainer, XAxis } from "recharts";

import Loader from "./../../Loader/Loader.js";
import { Metric, StandardTooltip, selectFormat } from "./../../Utils/Utils.js";
import { XRayTooltip } from "./../../Utils/XrayUtils.js";

import Heading from "./../../Elements/Heading";

import { getXrayTooltipPosition } from "../../../helpers/DOM";

const color = {
  subscribers: "#FC6D52",
  engagements: "#007bff",
  views: "#28a745",
  avg_watch_time: "#F11F8E",
  videos_published: "#FBD14A",
  unique_viewers: "#F11F8E",
};

class YoutubeKeyStats extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltipSubscribers: {
        show: false,
        x: 0,
        y: 0,
      },
      tooltipEngagements: {
        show: false,
        x: 0,
        y: 0,
      },
      tooltipViews: {
        show: false,
        x: 0,
        y: 0,
      },
      tooltipAvgWatchTime: {
        show: false,
        x: 0,
        y: 0,
      },
      tooltipVideosPublished: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.node = React.createRef();
    this.subscribersPosition = React.createRef();
    this.engagementsPosition = React.createRef();
    this.viewsPosition = React.createRef();
    this.avgWatchTimePosition = React.createRef();
    this.videosPublishedPosition = React.createRef();

    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltipSubscribers();
    this.closeTooltipEngagements();
    this.closeTooltipViews();
    this.closeTooltipAvgWatchTime();
    this.closeTooltipVideosPublished();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltipSubscribers = (event) => {
    this.setState({
      tooltipSubscribers: { show: false },
    });
  };

  closeTooltipEngagements = (event) => {
    this.setState({
      tooltipEngagements: { show: false },
    });
  };

  closeTooltipViews = (event) => {
    this.setState({
      tooltipViews: { show: false },
    });
  };

  closeTooltipAvgWatchTime = (event) => {
    this.setState({
      tooltipAvgWatchTime: { show: false },
    });
  };

  closeTooltipVideosPublished = (event) => {
    this.setState({
      tooltipVideosPublished: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (this.subscribersPosition) {
      if (
        this.subscribersPosition.current &&
        !this.subscribersPosition.current.contains(e.target)
      ) {
        this.closeTooltipSubscribers();
      }
    }

    if (this.engagementsPosition) {
      if (
        this.engagementsPosition.current &&
        !this.engagementsPosition.current.contains(e.target)
      ) {
        this.closeTooltipEngagements();
      }
    }

    if (this.viewsPosition) {
      if (
        this.viewsPosition.current &&
        !this.viewsPosition.current.contains(e.target)
      ) {
        this.closeTooltipViews();
      }
    }

    if (this.avgWatchTimePosition) {
      if (
        this.avgWatchTimePosition.current &&
        !this.avgWatchTimePosition.current.contains(e.target)
      ) {
        this.closeTooltipAvgWatchTime();
      }
    }

    if (this.videosPublishedPosition) {
      if (
        this.videosPublishedPosition.current &&
        !this.videosPublishedPosition.current.contains(e.target)
      ) {
        this.closeTooltipVideosPublished();
      }
    }
  };

  getExportData = () => ({
    data: [
      {
        Metric: "Total Subscribers",
        Value: selectFormat(
          "number",
          this.props.data.metrics.subscribers.value,
          "0,0"
        ),
      },
      {
        Metric: "Engagements",
        Value: selectFormat(
          "number",
          this.props.data.metrics.engagements.value,
          "0,0"
        ),
      },
      {
        Metric: "Views",
        Value: selectFormat(
          "number",
          this.props.data.metrics.views.value,
          "0,0"
        ),
      },
      {
        Metric: "Avg Watch Time",
        Value: selectFormat(
          "minsec",
          this.props.data.metrics.avg_watch_time.value,
          "0,0"
        ),
      },
      {
        Metric: "Unique Videos Published",
        Value: selectFormat(
          "number",
          this.props.data.metrics.videos_published.value,
          "0,0"
        ),
      },
    ],
    opts: {
      type: "other",
    },
  });

  render() {
    const { loading } = this.props;

    if (loading) {
      return <Loader minHeight={340} col={"col-12 col-md-6"} />;
    }

    const data = this.props.data;
    return (
      <div className="row key-stats border-between-metrics">
        <div
          className="col-lg-2 col-sm-6 col-12 key-stats--item"
          ref={this.subscribersPosition}
        >
          <div className="row row--header">
            <div className="col-12">
              <Heading size={5}>Total Subscribers</Heading>

              <Metric {...data.metrics.subscribers} size={"md"} />
            </div>
          </div>

          <ResponsiveContainer height={230} width="100%" className="mt-3">
            <BarChart
              data={data.metricsByDay.map((d) => ({
                date: d.date,
                value: d.subscribers,
              }))}
              onClick={(d) => {
                const extra = [
                  {
                    date: "Total Subscribers",
                    key: selectFormat(
                      "date",
                      d.activePayload[0].payload.date,
                      "MM/DD"
                    ),
                    value: d.activePayload[0].payload.value,
                    color: "#FC6D52",
                  },
                ];
                d.extra = extra;
                this.setState({
                  tooltipSubscribers: { ...d, show: true },
                });
              }}
            >
              {!this.state.tooltipSubscribers.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          return (
                            <div className="standard-tooltip">
                              {Object.keys(payload).map((key) => (
                                <Fragment key={key}>
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      New Subscribers
                                    </span>
                                  </header>
                                  <main className="standard-tooltip__main">
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: color.subscribers,
                                          }}
                                        />
                                        <span className="standard-tooltip__value-header">
                                          {selectFormat(
                                            "date",
                                            payload[key].payload.date,
                                            "MM/DD"
                                          )}
                                        </span>
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          payload[key].value
                                        )}
                                      </span>
                                    </div>
                                  </main>
                                </Fragment>
                              ))}
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              <defs>
                <linearGradient
                  id={`color-${color.subscribers}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color.subscribers}
                    stopOpacity={0.55}
                  />
                  <stop
                    offset="95%"
                    stopColor={color.subscribers}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                fillOpacity={1}
                fill={`url(#color-${color.subscribers})`}
                cursor="pointer"
              />
              <XAxis dataKey="date" hide />
            </BarChart>
          </ResponsiveContainer>
          {this.state.tooltipSubscribers.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top:
                  this.state.tooltipSubscribers.activeCoordinate.y + 80 + "px",
                left:
                  this.state.tooltipSubscribers.activeCoordinate.x + 220 <
                  this.state.width
                    ? this.state.tooltipSubscribers.activeCoordinate.x +
                      15 +
                      "px"
                    : this.state.tooltipSubscribers.activeCoordinate.x +
                      50 +
                      "px" -
                      getXrayTooltipPosition(
                        this.subscribersPosition.current,
                        this.state.tooltipSubscribers.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltipSubscribers.show ? 1.0 : 0.0,
                visibility: this.state.tooltipSubscribers.show
                  ? "visible"
                  : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltipSubscribers}
                close={this.closeTooltipSubscribers.bind(this)}
              />
            </div>
          ) : null}
        </div>

        <div
          className="col-lg-2 col-sm-6 col-12 key-stats--item"
          ref={this.engagementsPosition}
        >
          <div className="row row--header">
            <div className="col-12">
              <Heading size={5}>Engagements</Heading>

              <Metric {...data.metrics.engagements} size={"md"} />
            </div>
          </div>

          <ResponsiveContainer height={230} width="100%" className="mt-3">
            <BarChart
              data={data.metricsByDay.map((d) => ({
                date: d.date,
                value: d.engagements,
              }))}
              onClick={(d) => {
                const extra = [
                  {
                    date: "Engagements",
                    key: selectFormat(
                      "date",
                      d.activePayload[0].payload.date,
                      "MM/DD"
                    ),
                    value: d.activePayload[0].payload.value,
                    color: "#007bff",
                  },
                ];
                d.extra = extra;
                this.setState({
                  tooltipEngagements: { ...d, show: true },
                });
              }}
            >
              {!this.state.tooltipEngagements.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          return (
                            <div className="standard-tooltip">
                              {Object.keys(payload).map((key) => (
                                <Fragment key={key}>
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Engagements
                                    </span>
                                  </header>
                                  <main className="standard-tooltip__main">
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: color.engagements,
                                          }}
                                        />
                                        <span className="standard-tooltip__value-header">
                                          {selectFormat(
                                            "date",
                                            payload[key].payload.date,
                                            "MM/DD"
                                          )}
                                        </span>
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          payload[key].value
                                        )}
                                      </span>
                                    </div>
                                  </main>
                                </Fragment>
                              ))}
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              <defs>
                <linearGradient
                  id={`color-${color.engagements}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color.engagements}
                    stopOpacity={0.55}
                  />
                  <stop
                    offset="95%"
                    stopColor={color.engagements}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                fillOpacity={1}
                fill={`url(#color-${color.engagements})`}
                cursor="pointer"
              />
              <XAxis dataKey="date" hide />
              {/* {goal ? <ReferenceLine y={goal.engagements} strokeDasharray="3 3" stroke="#FC6D52" ifOverflow="extendDomain" /> : null} */}
            </BarChart>
          </ResponsiveContainer>
          {this.state.tooltipEngagements.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top:
                  this.state.tooltipEngagements.activeCoordinate.y + 80 + "px",
                left:
                  this.state.tooltipEngagements.activeCoordinate.x + 220 <
                  this.state.width
                    ? this.state.tooltipEngagements.activeCoordinate.x +
                      15 +
                      "px"
                    : this.state.tooltipEngagements.activeCoordinate.x +
                      50 +
                      "px" -
                      getXrayTooltipPosition(
                        this.engagementsPosition.current,
                        this.state.tooltipEngagements.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltipEngagements.show ? 1.0 : 0.0,
                visibility: this.state.tooltipEngagements.show
                  ? "visible"
                  : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltipEngagements}
                close={this.closeTooltipEngagements.bind(this)}
              />
            </div>
          ) : null}
        </div>

        <div
          className="col-lg-2 col-sm-6 col-12 key-stats--item"
          ref={this.viewsPosition}
        >
          <div className="row row--header">
            <div className="col-12">
              <Heading size={5}>Views</Heading>

              <Metric {...data.metrics.views} size={"md"} />
            </div>
          </div>

          <ResponsiveContainer height={230} width="100%" className="mt-3">
            <BarChart
              data={data.metricsByDay.map((d) => ({
                date: d.date,
                value: d.views,
              }))}
              onClick={(d) => {
                const extra = [
                  {
                    date: "Views",
                    key: selectFormat(
                      "date",
                      d.activePayload[0].payload.date,
                      "MM/DD"
                    ),
                    value: d.activePayload[0].payload.value,
                    color: "#28a745",
                  },
                ];
                d.extra = extra;
                this.setState({
                  tooltipViews: { ...d, show: true },
                });
              }}
            >
              {!this.state.tooltipViews.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          return (
                            <div className="standard-tooltip">
                              {Object.keys(payload).map((key) => (
                                <Fragment key={key}>
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Views
                                    </span>
                                  </header>
                                  <main className="standard-tooltip__main">
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{ background: color.views }}
                                        />
                                        <span className="standard-tooltip__value-header">
                                          {selectFormat(
                                            "date",
                                            payload[key].payload.date,
                                            "MM/DD"
                                          )}
                                        </span>
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          payload[key].value
                                        )}
                                      </span>
                                    </div>
                                  </main>
                                </Fragment>
                              ))}
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              <defs>
                <linearGradient
                  id={`color-${color.views}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color.views}
                    stopOpacity={0.55}
                  />
                  <stop offset="95%" stopColor={color.views} stopOpacity={1} />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                fillOpacity={1}
                fill={`url(#color-${color.views})`}
                cursor="pointer"
              />
              <XAxis dataKey="date" hide />
            </BarChart>
          </ResponsiveContainer>
          {this.state.tooltipViews.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top: this.state.tooltipViews.activeCoordinate.y + 80 + "px",
                left:
                  this.state.tooltipViews.activeCoordinate.x + 220 <
                  this.state.width
                    ? this.state.tooltipViews.activeCoordinate.x + 15 + "px"
                    : this.state.tooltipViews.activeCoordinate.x +
                      50 +
                      "px" -
                      getXrayTooltipPosition(
                        this.viewsPosition.current,
                        this.state.tooltipViews.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltipViews.show ? 1.0 : 0.0,
                visibility: this.state.tooltipViews.show ? "visible" : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltipViews}
                close={this.closeTooltipViews.bind(this)}
              />
            </div>
          ) : null}
        </div>

        <div
          className="col-lg-2 col-sm-6 col-12 key-stats--item"
          ref={this.avgWatchTimePosition}
        >
          <div className="row row--header">
            <div className="col-12">
              <Heading size={5}>Avg Watch Time</Heading>

              <Metric
                {...data.metrics.avg_watch_time}
                size={"md"}
                type={"minsec"}
              />
            </div>
          </div>

          <ResponsiveContainer height={230} width="100%" className="mt-3">
            <BarChart
              data={data.metricsByDay.map((d) => ({
                date: d.date,
                value: d.avg_watch_time,
              }))}
              onClick={(d) => {
                const extra = [
                  {
                    date: "Avg Watch Time",
                    key: selectFormat(
                      "date",
                      d.activePayload[0].payload.date,
                      "MM/DD"
                    ),
                    value: d.activePayload[0].payload.value,
                    keyStatsValue: "keyStatsValue",
                    color: "#F11F8E",
                  },
                ];
                d.extra = extra;
                this.setState({
                  tooltipAvgWatchTime: { ...d, show: true },
                });
              }}
            >
              {!this.state.tooltipAvgWatchTime.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          return (
                            <div className="standard-tooltip">
                              {Object.keys(payload).map((key) => (
                                <Fragment key={key}>
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Avg Watch Time
                                    </span>
                                  </header>
                                  <main className="standard-tooltip__main">
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: color.avg_watch_time,
                                          }}
                                        />
                                        <span className="standard-tooltip__value-header">
                                          {selectFormat(
                                            "date",
                                            payload[key].payload.date,
                                            "MM/DD"
                                          )}
                                        </span>
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "minsec",
                                          payload[key].value
                                        )}
                                      </span>
                                    </div>
                                  </main>
                                </Fragment>
                              ))}
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              <defs>
                <linearGradient
                  id={`color-${color.avg_watch_time}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color.avg_watch_time}
                    stopOpacity={0.55}
                  />
                  <stop
                    offset="95%"
                    stopColor={color.avg_watch_time}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                fillOpacity={1}
                fill={`url(#color-${color.avg_watch_time})`}
                cursor="pointer"
              />
              <XAxis dataKey="date" hide />
            </BarChart>
          </ResponsiveContainer>
          {this.state.tooltipAvgWatchTime.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top:
                  this.state.tooltipAvgWatchTime.activeCoordinate.y + 80 + "px",
                left:
                  this.state.tooltipAvgWatchTime.activeCoordinate.x + 220 <
                  this.state.width
                    ? this.state.tooltipAvgWatchTime.activeCoordinate.x +
                      15 +
                      "px"
                    : this.state.tooltipAvgWatchTime.activeCoordinate.x +
                      50 +
                      "px" -
                      getXrayTooltipPosition(
                        this.avgWatchTimePosition.current,
                        this.state.tooltipAvgWatchTime.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltipAvgWatchTime.show ? 1.0 : 0.0,
                visibility: this.state.tooltipAvgWatchTime.show
                  ? "visible"
                  : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltipAvgWatchTime}
                close={this.closeTooltipAvgWatchTime.bind(this)}
              />
            </div>
          ) : null}
        </div>

        <div
          className="col-lg-2 col-sm-6 col-12 key-stats--item"
          ref={this.videosPublishedPosition}
        >
          <div className="row row--header">
            <div className="col-12">
              <Heading size={5}>Unique Videos Published</Heading>

              <Metric {...data.metrics.videos_published} size={"md"} />
            </div>
          </div>

          <ResponsiveContainer height={230} width="100%" className="mt-3">
            <BarChart
              data={data.metricsByDay.map((d) => ({
                date: d.date,
                value: d.videos_published,
              }))}
              onClick={(d) => {
                const extra = [
                  {
                    date: "Videos Published",
                    key: selectFormat(
                      "date",
                      d.activePayload[0].payload.date,
                      "MM/DD"
                    ),
                    value: d.activePayload[0].payload.value,
                    color: "#FBD14A",
                  },
                ];
                d.extra = extra;
                this.setState({
                  tooltipVideosPublished: { ...d, show: true },
                });
              }}
            >
              {!this.state.tooltipVideosPublished.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(this.props);
                        if (payload !== null) {
                          return (
                            <div className="standard-tooltip">
                              {Object.keys(payload).map((key) => (
                                <Fragment key={key}>
                                  <header className="standard-tooltip__header">
                                    <span className="standard-tooltip__key-header">
                                      Videos Published
                                    </span>
                                  </header>
                                  <main className="standard-tooltip__main">
                                    <div className="standard-tooltip__item">
                                      <span className="standard-tooltip__key">
                                        <span
                                          className="standard-tooltip__icon"
                                          style={{
                                            background: color.videos_published,
                                          }}
                                        />
                                        <span className="standard-tooltip__value-header">
                                          {selectFormat(
                                            "date",
                                            payload[key].payload.date,
                                            "MM/DD"
                                          )}
                                        </span>
                                      </span>
                                      <span className="standard-tooltip__value">
                                        {selectFormat(
                                          "number",
                                          payload[key].value
                                        )}
                                      </span>
                                    </div>
                                  </main>
                                </Fragment>
                              ))}
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              <defs>
                <linearGradient
                  id={`color-${color.videos_published}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color.videos_published}
                    stopOpacity={0.55}
                  />
                  <stop
                    offset="95%"
                    stopColor={color.videos_published}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                fillOpacity={1}
                fill={`url(#color-${color.videos_published})`}
                cursor="pointer"
              />
              <XAxis dataKey="date" hide />
            </BarChart>
          </ResponsiveContainer>
          {this.state.tooltipVideosPublished.activeCoordinate ? (
            <div
              className="xray-tooltip-container"
              style={{
                top:
                  this.state.tooltipVideosPublished.activeCoordinate.y +
                  80 +
                  "px",
                left:
                  this.state.tooltipVideosPublished.activeCoordinate.x + 220 <
                  this.state.width
                    ? this.state.tooltipVideosPublished.activeCoordinate.x +
                      15 +
                      "px"
                    : this.state.tooltipVideosPublished.activeCoordinate.x +
                      50 +
                      "px" -
                      getXrayTooltipPosition(
                        this.videosPublishedPosition.current,
                        this.state.tooltipVideosPublished.activeCoordinate
                      ) +
                      "px",
                opacity: this.state.tooltipVideosPublished.show ? 1.0 : 0.0,
                visibility: this.state.tooltipVideosPublished.show
                  ? "visible"
                  : "hidden",
              }}
            >
              <XRayTooltip
                data={this.state.tooltipVideosPublished}
                close={this.closeTooltipVideosPublished.bind(this)}
              />
            </div>
          ) : null}
        </div>

        {/* <div className="col-lg-2 col-sm-6 col-12 key-stats--item">
          <div className="row row--header">
            <div className="col-12">
              <Heading size={5}>Unique Viewers</Heading>
              <Metric {...data.metrics.unique_viewers} size={"md"} />
            </div>
          </div>

          <ResponsiveContainer height={230} width="100%" className="mt-3">
            <BarChart
              data={data.metricsByDay.map((d) => ({
                date: d.date,
                value: d.unique_viewers,
              }))}
            >
              <Tooltip
                cursor={{ fill: "#eee", opacity: 0.5 }}
                content={
                  <StandardTooltip
                    render={({ data }) => {
                      const payload = data(this.props);
                      if (payload !== null) {
                        return (
                          <div className="standard-tooltip">
                            {Object.keys(payload).map((key) => (
                              <Fragment key={key}>
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Unique Viewers
                                  </span>
                                </header>
                                <main className="standard-tooltip__main">
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: color.unique_viewers,
                                        }}
                                      />
                                      <span className="standard-tooltip__value-header">
                                        {selectFormat(
                                          "date",
                                          payload[key].payload.date,
                                          "MM/DD"
                                        )}
                                      </span>
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        payload[key].value
                                      )}
                                    </span>
                                  </div>
                                </main>
                              </Fragment>
                            ))}
                          </div>
                        );
                      }
                    }}
                  />
                }
              />
              <defs>
                <linearGradient
                  id={`color-${color.unique_viewers}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={color.unique_viewers}
                    stopOpacity={0.55}
                  />
                  <stop
                    offset="95%"
                    stopColor={color.unique_viewers}
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <Bar
                dataKey="value"
                fillOpacity={1}
                fill={`url(#color-${color.unique_viewers})`}
              />
              <XAxis dataKey="date" hide />
            </BarChart>
          </ResponsiveContainer> 
       </div> */}
      </div>
    );
  }
}

export default YoutubeKeyStats;
